import CloseIcon from "@mui/icons-material/Close";
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
} from "@mui/material";
import { Button } from "pepsico-ds";
import { useState } from "react";
import { uploadWithPolling } from "../../../utils/fileTransfer";
import JsonFormsWrapper from "../../jsonForms/jsonFormsWrapper/JsonFormsWrapper";
import { downloadSegmentUsersTemplate } from "../utils/downloadSegmentUsersTemplate";
import "./_segment-dialog-modal.scss";

const jsonSchema = {
  type: "object",
  properties: {
    file: {
      type: "array",
      items: { type: "object" },
      title: "Upload a CSV file with users for this segment",
      minItems: 1,
    },
  },
  required: ["file"],
};

const uiSchema = {
  type: "VerticalLayout",
  elements: [
    {
      component: "simpleFileUpload",
      scope: "#/properties/file",
      type: "Control",
      accept: ".csv",
      dragDropText: "Drag and drop your .csv file here",
      multiple: false,
    },
  ],
};

const SegmentUsersImportModal = ({
  open,
  handleClose,
  segmentId,
  refetchSegmentUsers,
  showToast,
}) => {
  const [file, setFile] = useState({});
  const [uploading, setUploading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [doNotShowCheck, setDoNotShowCheck] = useState(false);

  const onModalClose = () => {
    setFile({});
    handleClose?.();
    confirmModalClose();
  };

  const confirmModalClose = () => {
    setConfirmOpen(false);
    setDoNotShowCheck(false);
  };

  const onDoNotShowCheckChange = (e) => {
    setDoNotShowCheck(e.target.checked);
  };

  const showConfirmationModal = () => {
    if (!file?.file) return;
    const flag = localStorage.getItem("doNotShowImportSegmentUsersModal");
    if (flag === "true") {
      return handleImport();
    }
    setConfirmOpen(true);
  };

  const handleImport = async (setLocalValue = false) => {
    if (!file?.file) return;
    setConfirmOpen(false);
    if (setLocalValue) {
      localStorage.setItem("doNotShowImportSegmentUsersModal", doNotShowCheck);
    }
    try {
      setUploading(true);
      const endpointBaseURI = window.REWARD_PARTNER_GROUP_FILE_MANAGER_BASE_URI;
      await uploadWithPolling({
        endpointBaseURI,
        context: "PLATFORM_SEGMENTS",
        file: file.file,
        metadata: { segment_id: segmentId },
        autoProcess: true,
        forceUpload: true,
        importFor: "platformSegments",
      });
      refetchSegmentUsers?.();
      onModalClose();
      showToast?.("success", "Segment users imported successfully");
    } catch (e) {
      console.error("Segment users import failed:", e);
      showToast?.("error", `Segment users import failed. ${e.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onModalClose}
        className="segment-dialog-modal view"
        fullWidth
        maxWidth={false}
      >
        <DialogTitle>
          <Grid container justifyContent="space-between" direction="row">
            <Grid item xs={10}>
              <h2>Import segment users</h2>
            </Grid>
            <Grid item xs={1} display="flex" justifyContent="flex-end">
              <CloseIcon onClick={onModalClose} className="cursor-pointer" />
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <div className="dialog-content">
            <JsonFormsWrapper
              jsonschema={jsonSchema}
              uischema={uiSchema}
              data={file}
              onChange={(data) => setFile(data)}
            />
            {file?.file?.name && (
              <div className="mt-2">
                <span className="font-xs">{file?.file?.name}</span>
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button variant="tertiary" text="Cancel" onClick={onModalClose} />
          <Button
            variant="secondary"
            text="Download template"
            onClick={() => downloadSegmentUsersTemplate(segmentId)}
          />
          <Button
            variant="primary"
            text={uploading ? "Uploading..." : "Import users"}
            disabled={!file?.file?.name || uploading}
            onClick={showConfirmationModal}
            isLoading={uploading}
          />
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmOpen}
        onClose={confirmModalClose}
        className="segment-dialog-modal view confirm-file-modal"
        fullWidth={false}
        maxWidth={"xs"}
      >
        <DialogContent>
          <div className="dialog-content">
            <div className="font-lg font-bold">
              Are you sure you want to import users for this segment with the
              uploaded file?
            </div>
            <div className="mb-2">
              <h6>*Once confirmed, import will be processed automatically.</h6>
            </div>
            <div>
              <FormControlLabel
                control={
                  <Checkbox
                    id={`checkbox-confirm-file`}
                    onChange={onDoNotShowCheckChange}
                  />
                }
                label="Do not show this message again"
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button variant="tertiary" text="Cancel" onClick={confirmModalClose} />
          <Button
            variant="primary"
            text={"Confirm"}
            disabled={!file?.file?.name || uploading}
            onClick={() => handleImport(true)}
            isLoading={uploading}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SegmentUsersImportModal;
